import type { MouseEventHandler, SVGProps } from "react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FloatingTooltip } from "src/common/components/Tooltip/FloatingTooltip";
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";
import NodeInfo from "./NodeInfo";
import { formatContentTypeName } from "../../util/NameFormatter";
import type { Content } from "../../util/dataTypes/Content";
import type { ObjectScene } from "../../util/dataTypes/ContentType";
import "./SceneNode.scss";
import {
  DocumentDuplicateIcon,
  DuplicateIcon,
  TemplateIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { TruncatedText } from "../../../../common/components/TruncatedText/TruncatedText";

export type Props = SVGProps<SVGSVGElement> & {
  title: string;
  valid?: boolean;
  onClick?: MouseEventHandler<SVGSVGElement>;
  sceneType?: string;
  content?: Content;
  ctName?: string;
  details?: ObjectScene;
  unlinked?: boolean;
  hideInfo?: boolean;
  onChromeMouseEnter?: React.MouseEventHandler<SVGElement>;
  onChromeMouseLeave?: React.MouseEventHandler<SVGElement>;
  hideMenuButton?: boolean;
  startScene?: boolean;
  width?: number | string;
  height?: number | string;
  viewBox?: string;
  onCopyScene?: (info: { contentSceneId: number; contentId: number }) => void;
  onDuplicateScene?: (info: { contentSceneId: number; contentId: number }) => void;
  onRelinkScene?: (info: { contentSceneId: number; contentId: number; name?: string }) => void;
  onCreateLinkedScene?: (info: {
    contentSceneId: number;
    contentId: number;
    name?: string;
  }) => void;
  onDelinkScene?: (info: { contentSceneId: number; contentId: number; name?: string }) => void;
  onDeleteScene?: (info: { contentSceneId: number; contentId: number; name?: string }) => void;
  onSetStartScene?: (info: { contentSceneId: number; contentId: number; name?: string }) => void;
  unlinkedInteractions?: { contentId: number; modelName: string; name: string; ctId: number }[];
  onEditUnlinked?: (info: { modelName: string; ctId: number }) => void;
};

type Sized = {
  HEIGHT: number;
  WIDTH: number;
};

const SVGNodeScene: React.FC<Props> & Sized = ({
  title,
  valid,
  onClick,
  content,
  sceneType,
  ctName,
  details,
  startScene,
  width,
  height,
  viewBox,
  unlinked,
  hideInfo,
  onChromeMouseEnter,
  onChromeMouseLeave,
  hideMenuButton,
  onCopyScene,
  onDuplicateScene,
  onRelinkScene,
  onCreateLinkedScene,
  onDelinkScene,
  onDeleteScene,
  onSetStartScene,
  unlinkedInteractions,
  onEditUnlinked,
  ...svgProps
}: Props) => {
  const x = svgProps.x;
  const { t } = useTranslation();

  const tspans = useMemo(() => buildTSpans(title), [title]);
  const strokeWidth = 2;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [menuOpen]);

  const handlePlusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
    setMenuOpen((prev) => !prev);
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onCopyScene && details && content) {
      onCopyScene({ contentSceneId: details.id, contentId: content.id });
    }
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onDuplicateScene && details && content) {
      onDuplicateScene({ contentSceneId: details.id, contentId: content.id });
    }
  };

  const handleCreateLinkedClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onCreateLinkedScene && details && content) {
      onCreateLinkedScene({ contentSceneId: details.id, contentId: content.id, name: title });
    }
  };

  const handleRelinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onRelinkScene && details && content) {
      onRelinkScene({ contentSceneId: details.id, contentId: content.id, name: title });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onDeleteScene && details && content) {
      onDeleteScene({ contentSceneId: details.id, contentId: content.id, name: title });
    }
  };

  const handleDelinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onDelinkScene && details && content) {
      onDelinkScene({ contentSceneId: details.id, contentId: content.id, name: title });
    }
  };

  const handleSetStartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onSetStartScene && details && content) {
      onSetStartScene({ contentSceneId: details.id, contentId: content.id, name: title });
    }
  };

  const unlinkedCount = unlinkedInteractions?.length ?? 0;
  const [unlinkedOpen, setUnlinkedOpen] = useState(false);
  const [unlinkedPos, setUnlinkedPos] = useState({ top: 0, left: 0 });
  const unlinkedRef = useRef<HTMLDivElement>(null);
  const unlinkedBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!unlinkedOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (unlinkedBtnRef.current && unlinkedBtnRef.current.contains(target)) return;
      if (unlinkedRef.current && !unlinkedRef.current.contains(target)) {
        setUnlinkedOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [unlinkedOpen]);

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setUnlinkedPos({ top: rect.bottom + 4, left: rect.left });
    setUnlinkedOpen((prev) => !prev);
  };

  const plusBtnX = startScene ? 82 : 72;
  const plusBtnY = startScene ? 78 : 72;

  return (
    <svg
      width={width ?? SVGNodeScene.WIDTH}
      height={height ?? SVGNodeScene.HEIGHT}
      viewBox={viewBox ?? "0 0 100 120"}
      onClick={onClick}
      {...svgProps}
      className={"thenode " + (svgProps.className ?? "")}
    >
      <g fill="none">
        {!hideInfo && (
          <NodeInfo
            x="75"
            y="20"
            type="scene"
            infoText={`Scene ${sceneType}`}
            infoTitle={ctName}
            contentId={content?.id}
            parentX={Number(x)}
            parentY={0}
            parentHeight={height ?? SVGNodeScene.HEIGHT}
            parentWidth={width ?? SVGNodeScene.WIDTH}
            onMouseEnter={onChromeMouseEnter}
            onMouseLeave={onChromeMouseLeave}
          />
        )}
      </g>
      <g>
        <title>{title}</title>
        {startScene ? (
          <polygon
            points="25,0 25,120 100,60"
            stroke="rgb(118,126,133)"
            strokeWidth={strokeWidth}
            fill={valid ? "#02FCA5" : "rgb(73,80,87)"}
            id="start-scene-node"
          />
        ) : (
          <circle
            stroke="rgb(118,126,133)"
            strokeWidth={strokeWidth}
            // fill={valid ? "#02FCA5" : "#03154D"} #fc0303
            fill={
              // startScene ? "#000000" :      this was to ensure props.startScene being passed correctly
              valid ? "#02FCA5" : "rgb(73,80,87)"
            }
            cx="50%"
            cy="50%"
            x="0"
            y="0"
            r={39.5}
            filter={undefined}
            className="tour-scene-circle"
          />
        )}
        <text
          fontSize={13}
          letterSpacing={-0.336}
          fill={valid ? "#010038" : "#FFFFFF"}
          textAnchor="middle"
        >
          {tspans}
        </text>
      </g>
      {onCopyScene && (!hideMenuButton || menuOpen) && (
        <foreignObject
          x={plusBtnX}
          y={plusBtnY}
          width="22"
          height="22"
          pointerEvents="auto"
          onMouseEnter={onChromeMouseEnter}
          onMouseLeave={onChromeMouseLeave}
          style={{ overflow: "visible" }}
        >
          <FloatingTooltip content={t("pages.project.edit.copyToAnotherModule")} ariaHidden>
            <button
              onClick={handlePlusClick}
              aria-label={t("pages.project.edit.copyToAnotherModule")}
              className="nodrag flex items-center justify-center rounded-full shadow-md border border-gray-300 hover:scale-110 transition-transform"
              style={{
                width: 20,
                height: 20,
                backgroundColor: "#00C889",
                color: "white",
                fontSize: 14,
                fontWeight: "bold",
                lineHeight: 1,
                cursor: "pointer",
                padding: 0,
              }}
            >
              +
            </button>
          </FloatingTooltip>
        </foreignObject>
      )}
      {onEditUnlinked && unlinkedCount > 0 && (
        <foreignObject
          x={2}
          y={2}
          width="40"
          height="22"
          pointerEvents="auto"
          onMouseEnter={onChromeMouseEnter}
          onMouseLeave={onChromeMouseLeave}
          style={{ overflow: "visible" }}
        >
          <FloatingTooltip
            content={t("pages.project.edit.unlinkedInteractionsBadge", { count: unlinkedCount })}
            ariaHidden
          >
            <button
              ref={unlinkedBtnRef}
              aria-label={t("pages.project.edit.unlinkedInteractionsBadge", {
                count: unlinkedCount,
              })}
              onClick={handleBadgeClick}
              className="nodrag flex items-center gap-0.5 rounded-full shadow-sm border border-amber-300 hover:scale-105 transition-transform"
              style={{
                height: 18,
                padding: "0 6px",
                backgroundColor: "#fef3c7",
                color: "#b45309",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ⚠ {unlinkedCount}
            </button>
          </FloatingTooltip>
        </foreignObject>
      )}
      {unlinkedOpen &&
        createPortal(
          <div
            ref={unlinkedRef}
            className="bg-white border border-gray-200 rounded-md shadow-lg outline-none"
            style={{
              position: "fixed",
              top: unlinkedPos.top,
              left: unlinkedPos.left,
              zIndex: 50,
              minWidth: 240,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-700">
                {t("pages.project.edit.unlinkedInteractionsTitle")}
              </p>
              <p className="text-[11px] text-gray-400">
                {t("pages.project.edit.unlinkedInteractionsHint")}
              </p>
            </div>
            <div className="py-1">
              {(unlinkedInteractions ?? []).map(
                (it: { contentId: number; modelName: string; name: string; ctId: number }) => (
                  <button
                    key={it.contentId}
                    className="group flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnlinkedOpen(false);
                      onEditUnlinked?.({ modelName: it.modelName, ctId: it.ctId });
                    }}
                  >
                    <svg
                      className="w-4 h-4 stroke-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <TruncatedText
                      className="flex-1 text-left group-hover:text-green-600"
                      text={it.name}
                    />
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatContentTypeName(it.modelName)}
                    </span>
                  </button>
                ),
              )}
            </div>
          </div>,
          document.body,
        )}
      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 50,
              minWidth: 200,
            }}
          >
            {(onCreateLinkedScene || onRelinkScene || onSetStartScene || onDelinkScene) && (
              <div className="py-1">
                {onCreateLinkedScene && !unlinked && (
                  <button
                    className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={handleCreateLinkedClick}
                  >
                    <svg
                      className="mr-2 w-5 h-5 stroke-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t("pages.project.edit.createLinkedScene")}
                  </button>
                )}
                {onRelinkScene &&
                  (startScene ? (
                    <FloatingTooltip
                      content={t("pages.project.edit.relinkStartSceneDisabled")}
                      wrap
                      wrapperClassName="flex w-full"
                    >
                      <button
                        disabled
                        className="group flex w-full items-center rounded-md px-3 py-1 text-sm cursor-not-allowed text-gray-400"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          className="mr-2 w-5 h-5 stroke-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
                          />
                        </svg>
                        {t("pages.project.edit.replaceInModule")}
                      </button>
                    </FloatingTooltip>
                  ) : (
                    <button
                      className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                      onClick={handleRelinkClick}
                    >
                      <svg
                        className="mr-2 w-5 h-5 stroke-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
                        />
                      </svg>
                      {t(
                        unlinked
                          ? "pages.project.edit.relinkToModule"
                          : "pages.project.edit.replaceInModule",
                      )}
                    </button>
                  ))}
                {onSetStartScene && !startScene && (
                  <button
                    className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={handleSetStartClick}
                  >
                    <svg
                      className="mr-2 w-5 h-5 stroke-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5"
                      />
                    </svg>
                    {t("pages.project.edit.setAsStartScene")}
                    <InfoIconWithTooltip
                      message={t("pages.project.firstSceneTooltip")}
                      className="ml-1"
                      nested
                    />
                  </button>
                )}
                {onDelinkScene && !unlinked && !startScene && (
                  <button
                    className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                    onClick={handleDelinkClick}
                  >
                    <svg
                      className="mr-2 w-5 h-5 stroke-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.828 10.172a4 4 0 010 5.656l-1.5 1.5m-2.121-2.121a4 4 0 010-5.656l1.5-1.5M4 4l16 16"
                      />
                    </svg>
                    {t("pages.project.edit.delinkFromModule")}
                  </button>
                )}
              </div>
            )}
            <div className="py-1">
              {onDuplicateScene && (
                <button
                  className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                  onClick={handleDuplicateClick}
                >
                  <DuplicateIcon className="mr-2 w-5 h-5 stroke-green-500" strokeWidth={2} />
                  {t("pages.project.edit.duplicateInModule")}
                </button>
              )}
              <button
                className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                onClick={handleCopyClick}
              >
                <DocumentDuplicateIcon className="mr-2 w-5 h-5 stroke-green-500" strokeWidth={2} />
                {t("pages.project.edit.copyToAnotherModule")}
              </button>
              <button
                disabled
                className="group flex w-full items-center rounded-md px-3 py-1 text-sm cursor-not-allowed text-gray-400"
                onClick={(e) => e.stopPropagation()}
              >
                <TemplateIcon className="mr-2 w-5 h-5 stroke-gray-400" strokeWidth={2} />
                {t("pages.project.edit.createTemplate")}
                <span className="ml-1 text-xs text-gray-300">({t("general.comingSoon")})</span>
              </button>
            </div>
            {onDeleteScene && !startScene && (
              <div className="py-1">
                <button
                  className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-red-600 hover:bg-red-50"
                  onClick={handleDeleteClick}
                >
                  <TrashIcon className="mr-2 w-5 h-5 stroke-red-500" strokeWidth={2} />
                  {t("pages.project.edit.deleteScene")}
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </svg>
  );
};

SVGNodeScene.WIDTH = 150;
SVGNodeScene.HEIGHT = 150;

// we want to display text into the scene components, but there are a few issues
// - positioning is painful (think "centering")
// - we CANNOT WRAP TEXT: it will be available in SVG2 and is partially supported,
//   but as of now the only reliable and multi-platform way to auto-wrap text is
// The function below will build 1 or 2 <tspan> elements to display the title:
const buildTSpans = (title: string): JSX.Element | JSX.Element[] | null => {
  const elements = title.split(/\s+/, 2);

  switch (elements.length) {
    case 0:
      return null;
    case 1:
      if (title.length > 8) title = title.substring(0, 5) + "...";
      return (
        <tspan x="50%" y="50%" key="title" alignmentBaseline="middle" className="unselectable-text">
          {title.toLocaleUpperCase()}
        </tspan>
      );
    default:
      // eslint-disable-next-line no-case-declarations
      let [topTitle, bottomTitle] = elements;
      if (topTitle.length > 8) topTitle = topTitle.substring(0, 5) + "...";
      if (bottomTitle.length > 8) bottomTitle = bottomTitle.substring(0, 5) + "...";
      return [
        <tspan key="top-title" x="50%" y={"45%"} className="unselectable-text">
          {topTitle.toLocaleUpperCase()}
        </tspan>,
        <tspan key="bottom-title" x="50%" y={"55%"} className="unselectable-text">
          {bottomTitle.toLocaleUpperCase()}
        </tspan>,
      ];
  }
};

export default SVGNodeScene;
